import * as Sentry from "@sentry/react"

import { withTimeout } from "@/lib/withTimeout"
import { storageHealth, type StorageStatus } from "@/platform"
import { toast } from "@fieldbook/ui/toaster"

// Persisted once per install so a surveyor who has already been told their
// device denied persistent storage isn't told again on every subsequent
// launch. Wrapped in try/catch everywhere it's touched below: a private
// browsing mode or a full storage quota can make localStorage throw.
const PERSISTENCE_DENIED_REPORTED_KEY =
  "fieldbook.storagePersistenceDeniedReported"

function hasReportedPersistenceDenied(): boolean {
  try {
    return localStorage.getItem(PERSISTENCE_DENIED_REPORTED_KEY) === "true"
  } catch {
    return false
  }
}

function markPersistenceDeniedReported(): void {
  try {
    localStorage.setItem(PERSISTENCE_DENIED_REPORTED_KEY, "true")
  } catch {
    // Best effort — worst case we log this again on the next launch.
  }
}

// Memoized so the main.tsx startup call and the toast-triggering component
// share one check rather than each requesting persistence and re-estimating
// storage independently.
let startupCheck: Promise<StorageStatus> | null = null

async function runStorageHealthCheck(): Promise<StorageStatus> {
  const persisted = await storageHealth.requestPersistence()
  Sentry.setTag(
    "storage_persisted",
    persisted === null ? "unsupported" : String(persisted),
  )

  const status = await storageHealth.getStatus()
  Sentry.setContext("storage", {
    usage: status.usage,
    quota: status.quota,
    deviceFree: status.deviceFree,
    level: status.level,
  })

  if (persisted === false && !hasReportedPersistenceDenied()) {
    markPersistenceDeniedReported()
    Sentry.captureMessage("Storage persistence request denied", {
      level: "warning",
    })
  }

  return status
}

/**
 * Requests persistent storage and records the outcome to Sentry. Call once,
 * as early as possible (after Sentry.init) from main.tsx. Safe to call again
 * (e.g. from a component that wants the resulting StorageStatus to decide
 * whether to show a low-storage toast) — later callers get the same result
 * without repeating the underlying platform calls.
 */
export function initStorageHealth(): Promise<StorageStatus> {
  if (!startupCheck) startupCheck = runStorageHealthCheck()
  return startupCheck
}

// Bounds the check so a stalled platform call can't leave a toast to pop up
// long after the surveyor has moved on from the capture.
const CAPTURE_CHECK_TIMEOUT_MS = 300

// In-memory only — resets on relaunch, which is fine, this is just spam
// avoidance within a single session rather than a durable "seen it" record.
const CAPTURE_WARNING_INTERVAL_MS = 10 * 60 * 1000
let lastCaptureWarningAt = 0

/**
 * Best-effort storage check surfaced when the surveyor returns from taking
 * photos (PhotosUploadField), before they take more. Never throws — this is a
 * heads-up, not a gate.
 */
export async function warnIfStorageLowAfterCapture(): Promise<void> {
  if (Date.now() - lastCaptureWarningAt < CAPTURE_WARNING_INTERVAL_MS) return

  try {
    const status = await withTimeout(
      storageHealth.getStatus(),
      CAPTURE_CHECK_TIMEOUT_MS,
      () => null,
    )
    if (!status || status.level === "ok") return

    lastCaptureWarningAt = Date.now()
    toast.warning(
      status.level === "critical"
        ? "Device storage is critically low — more photos may not save. Free up space or sync soon."
        : "Device storage is running low. Free up space or sync soon to avoid losing photos.",
    )
  } catch {
    // Best effort — never block or fail the capture over a storage check.
  }
}
